import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { Fleet } from "../../../../../../../models/fleet";
import RoutePlannerFleetService from "../../../../../../../services/route.planner/fleet.service";
import { FleetKeys, RoutePlannerKeys } from "../../../../../../../util/queries";

const useRoutePlannerAction = () => {
  const { t: translate } = useTranslation();
  const queryClient = useQueryClient();

  const useAddToRoutePlanner = () => {
    return useMutation({
      mutationKey: RoutePlannerKeys.createFleet(),
      mutationFn: (fleet: Fleet) => RoutePlannerFleetService.create(fleet),
      onMutate: () => {
        NotificationToast.info(translate("addingToRoutePlanner"));
      },
      onSuccess: () => {
        NotificationToast.success(translate("addedToRoutePlanner"));
        queryClient.invalidateQueries({ queryKey: [FleetKeys.all] });
        queryClient.refetchQueries({ queryKey: [FleetKeys.all] });
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        else NotificationToast.error(translate("couldNotAddToRoutePlanner"));
      },
    });
  };

  const useUpdateToRoutePlanner = () => {
    return useMutation({
      mutationKey: RoutePlannerKeys.updateFleet(),
      mutationFn: ({ id, fleet }: { id: string; fleet: Fleet }) =>
        RoutePlannerFleetService.update(id, fleet),
      onMutate: () => {
        NotificationToast.info(translate("updatingInRoutePlanner"));
      },
      onSuccess: () => {
        NotificationToast.success(translate("updatedInRoutePlanner"));
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        else NotificationToast.error(translate("couldNotUpdateInRoutePlanner"));
      },
    });
  };

  const useRemoveFromRoutePlanner = () => {
    return useMutation({
      mutationKey: RoutePlannerKeys.deleteFleet(),
      mutationFn: (id: number) => RoutePlannerFleetService.delete(id),
      onMutate: () => {
        NotificationToast.info(translate("removingFromRoutePlanner"));
      },
      onSuccess: () => {
        NotificationToast.success(translate("removedFromRoutePlanner"));
        queryClient.invalidateQueries({ queryKey: [FleetKeys.all] });
        queryClient.refetchQueries({ queryKey: [FleetKeys.all] });
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        else NotificationToast.error(translate("couldNotRemoveFromRoutePlanner"));
      },
    });
  };

  return { useAddToRoutePlanner, useRemoveFromRoutePlanner, useUpdateToRoutePlanner };
};

export default useRoutePlannerAction;
